import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { logout } from "@/lib/api";
import { getMyCollege, agentActions } from "@/lib/campusApi";
import { LayoutDashboard, GraduationCap, Users, Megaphone, BarChart3, Bell, UserCog, LogOut, Menu, X, Building2, Settings, Sparkles, Target } from "lucide-react";
import WishireLogo from "@/components/wishire/WishireLogo";
import CopilotDock from "@/components/campus/CopilotDock";

const NAV = [
  { to: "/campus", end: true, label: "Dashboard", icon: LayoutDashboard, testId: "cm-nav-dashboard" },
  { to: "/campus/ai", label: "AI Officer", icon: Sparkles, testId: "cm-nav-ai", module: "ai_officer" },
  { to: "/campus/playbook", label: "Playbook", icon: Target, testId: "cm-nav-playbook", module: "students" },
  { to: "/campus/students", label: "Students", icon: Users, testId: "cm-nav-students", module: "students" },
  { to: "/campus/drives", label: "Drives", icon: Megaphone, testId: "cm-nav-drives", module: "drives" },
  { to: "/campus/recruiters", label: "Recruiters", icon: Building2, testId: "cm-nav-recruiters", module: "recruiters" },
  { to: "/campus/alumni", label: "Alumni", icon: GraduationCap, testId: "cm-nav-alumni", module: "alumni" },
  { to: "/campus/announcements", label: "Announcements", icon: Bell, testId: "cm-nav-announcements", module: "announcements" },
  { to: "/campus/reports", label: "Reports", icon: BarChart3, testId: "cm-nav-reports", module: "reports" },
  { to: "/campus/team", label: "Team", icon: UserCog, testId: "cm-nav-team", module: "team" },
  { to: "/campus/profile", label: "Profile", icon: Settings, testId: "cm-nav-profile" },
];

export default function CampusLayout() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [college, setCollege] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiPending, setAiPending] = useState(0);

  useEffect(() => {
    agentActions().then(({ data }) => setAiPending((data.proposed || []).length)).catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data, status } = await getMyCollege();
        if (status === 404) { navigate("/campus/onboarding", { replace: true }); return; }
        setCollege(data);
      } catch (e) {
        navigate("/campus/onboarding", { replace: true });
      } finally { setLoading(false); }
    })();
  }, [navigate]);

  const handleLogout = async () => {
    try { await logout(); } catch (e) {}
    setUser(null);
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-[#FDFBF7] text-stone-900">
      <aside className={`fixed md:sticky inset-y-0 left-0 z-40 w-72 bg-stone-950 text-stone-100 flex flex-col transform transition-transform md:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"} md:h-screen md:top-0 border-r border-white/5`}>
        <div className="flex-shrink-0 px-5 pt-6">
          <button onClick={() => navigate("/")} className="flex items-baseline gap-1 mb-5 px-1" data-testid="cm-logo">
            <WishireLogo dark size="md" />
          </button>
          {college && (
            <div className="rounded-2xl border border-white/10 p-3.5 bg-gradient-to-br from-white/[0.07] to-white/[0.02]">
              <div className="flex items-center gap-3">
                {college.logo ? (
                  <img src={college.logo} alt={college.name} data-testid="cm-sidebar-logo" className="w-10 h-10 rounded-xl object-contain bg-white shadow-lg" />
                ) : (
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-300 to-amber-400 text-stone-900 flex items-center justify-center shadow-lg shadow-amber-500/20"><GraduationCap size={18} /></div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-stone-50 truncate" data-testid="cm-name">{college.name}</p>
                  <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-400/15 text-amber-300">
                    <span className="w-1 h-1 rounded-full bg-amber-400" /> Placement cell
                  </span>
                </div>
              </div>
              {college.invite_code && (
                <div className="mt-3 flex items-center justify-between rounded-xl bg-black/30 px-3 py-2">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-stone-400">Invite code</span>
                  <span className="font-mono text-sm tracking-widest text-amber-300" data-testid="cm-invite">{college.invite_code}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 min-h-0 co-scroll">
          <div className="space-y-0.5">
            {NAV.filter((n) => !n.module || !college?.modules || college.modules[n.module] !== false).map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testId} onClick={() => setOpen(false)}
                className={({ isActive }) => `group relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 ${isActive ? "bg-white/10 text-white" : "text-stone-400 hover:bg-white/5 hover:text-stone-100"}`}>
                {({ isActive }) => (
                  <>
                    {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-amber-300" />}
                    <n.icon size={18} className={isActive ? "text-amber-300" : "text-stone-500 group-hover:text-stone-300"} />
                    <span className="text-sm font-medium">{n.label}</span>
                    {n.to === "/campus/ai" && aiPending > 0 && (
                      <span data-testid="cm-nav-ai-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-amber-300 text-stone-900 text-[10px] font-bold flex items-center justify-center">{aiPending}</span>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="flex-shrink-0 px-3 pb-4 pt-3 border-t border-white/10 bg-stone-950">
          <div className="flex items-center gap-3 px-2 py-2 rounded-xl">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-300 to-amber-400 text-stone-900 font-display flex items-center justify-center shrink-0">{(user?.name || "?")[0]}</div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-stone-100 truncate">{user?.name}</p>
              <p className="text-xs text-stone-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button type="button" data-testid="cm-logout" onClick={handleLogout}
            className="mt-1.5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-stone-400 hover:bg-red-500/10 hover:text-red-300 transition-colors text-sm font-medium">
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </aside>

      <div className="md:hidden fixed top-0 inset-x-0 safe-top safe-x z-30 bg-[#FDFBF7]/90 backdrop-blur border-b border-stone-200 px-4 py-3 flex items-center justify-between">
        <WishireLogo size="sm" wordOnly />
        <button aria-label="Open menu" onClick={() => setOpen((v) => !v)} className="p-2 rounded-full border border-stone-300">{open ? <X size={16} /> : <Menu size={16} />}</button>
      </div>
      {open && <div className="md:hidden fixed inset-0 bg-black/40 z-30" onClick={() => setOpen(false)} />}

      <main className="flex-1 min-w-0 pt-16 md:pt-0">
        {loading ? (
          <div className="min-h-screen flex items-center justify-center text-stone-500">Loading workspace…</div>
        ) : (
          <Outlet context={{ college, setCollege }} />
        )}
      </main>

      {/* AI copilot, available everywhere in the workspace (when the module is enabled) */}
      {!loading && college?.modules?.ai_officer !== false && <CopilotDock />}
    </div>
  );
}
